import { hitProfileFor } from "./combat";
import {
  leashPullbackGoalBias,
  pickCombatTarget,
  shouldEngage,
  steerCombat,
  swingSecFor,
  tryFireAttack,
  tryInterruptMarchOnThreat,
} from "./combat-ai";
import {
  applyFacingFromIntent,
  setDesiredToward,
} from "./motion-steering";
import {
  SpatialHash2D,
  applySoftSepSpatial,
  clampSepImpulse,
  considerTopK,
  stepLocomotionAnim as stepLocomotionAnimEngine,
  type SepNeighbor,
} from "../../../sim";
import type { SimEntity, WorldState } from "./types";
import {
  C,
  canAttack,
  clampToPlay,
  dist,
} from "./shared";
import {
  onUnitMaybeArrived,
  shouldDeferCombat,
  tickMoveOrders,
} from "./goals";

export { applyFacingFromIntent, setDesiredToward } from "./motion-steering";
export { pickCombatTarget, pickTarget } from "./combat-ai";
export { SpatialHash2D } from "../../../sim";
export { lateralizeSepImpulse, clampSepImpulse } from "../../../sim";

function troopSlotOffset(e: SimEntity): { x: number; y: number } {
  const r = C.crowd.slotRadius;
  if (r <= 0) return { x: 0, y: 0 };
  const ang = (e.id * 2.399963229728653) % (Math.PI * 2);
  return { x: Math.cos(ang) * r, y: Math.sin(ang) * r };
}

type SepN = SepNeighbor<SimEntity>;

const sepHash = new SpatialHash2D<SimEntity>();
const sepQueryScratch: SimEntity[] = [];
const sepNeighborsScratch: SepN[] = [];

function defaultSepCellSize(sepRadiusMul: number): number {
  const configured = Number(
    (C.crowd as { sepCellSize?: number }).sepCellSize,
  );
  if (configured > 0) return configured;
  // master+master ≈ 43px at mul 1.2 — cell ≈ that radius.
  return Math.max(32, Math.ceil(36 * sepRadiusMul));
}

/** Fill top-k sep neighbors for each non-weapon entity (spatial hash). */
export function gatherSoftSepTopK(
  list: readonly SimEntity[],
  sepRadiusMul: number,
  maxK: number,
  cellSize: number,
): Map<number, number[]> {
  const k = Math.max(1, maxK);
  let maxR = 0;
  for (let i = 0; i < list.length; i++) {
    maxR = Math.max(maxR, list[i]!.radius);
  }
  sepHash.setCellSize(cellSize);
  sepHash.clear();
  for (let i = 0; i < list.length; i++) {
    const e = list[i]!;
    sepHash.insert(e.x, e.y, e);
  }
  const out = new Map<number, number[]>();
  for (let i = 0; i < list.length; i++) {
    const a = list[i]!;
    if (a.role === "weapon") continue;
    const queryR = (a.radius + maxR) * sepRadiusMul;
    sepQueryScratch.length = 0;
    sepHash.queryDisk(a.x, a.y, queryR, sepQueryScratch);
    sepNeighborsScratch.length = 0;
    for (let j = 0; j < sepQueryScratch.length; j++) {
      const b = sepQueryScratch[j]!;
      if (b.id === a.id) continue;
      const d = dist(a.x, a.y, b.x, b.y);
      const minD = (a.radius + b.radius) * sepRadiusMul;
      if (d < minD) considerTopK(sepNeighborsScratch, { e: b, d }, k);
    }
    out.set(
      a.id,
      sepNeighborsScratch.map((n) => n.e.id),
    );
  }
  return out;
}

/** Brute O(n²) top-k — regression oracle for spatial hash. */
export function gatherSoftSepTopKBrute(
  list: readonly SimEntity[],
  sepRadiusMul: number,
  maxK: number,
): Map<number, number[]> {
  const k = Math.max(1, maxK);
  const out = new Map<number, number[]>();
  for (let i = 0; i < list.length; i++) {
    const a = list[i]!;
    if (a.role === "weapon") continue;
    const neighbors: SepN[] = [];
    for (let j = 0; j < list.length; j++) {
      if (i === j) continue;
      const b = list[j]!;
      const d = dist(a.x, a.y, b.x, b.y);
      const minD = (a.radius + b.radius) * sepRadiusMul;
      if (d < minD) considerTopK(neighbors, { e: b, d }, k);
    }
    out.set(
      a.id,
      neighbors.map((n) => n.e.id),
    );
  }
  return out;
}

function applySoftSeparation(world: WorldState): void {
  const {
    sepRadiusMul,
    sepStrength,
    maxSepNeighbors,
    sepDamping,
    walkEnter,
    walkExit,
    restSepMul,
  } = C.crowd;
  const damp = Math.max(0, Math.min(1, sepDamping));
  const restMul = Math.max(0, Math.min(1, restSepMul));
  const maxImpulse =
    Number((C.crowd as { sepMaxImpulse?: number }).sepMaxImpulse) || 90;
  const list = world.entities;
  const k = Math.max(1, maxSepNeighbors);
  const cellSize = defaultSepCellSize(sepRadiusMul);

  let maxR = 0;
  for (let i = 0; i < list.length; i++) {
    maxR = Math.max(maxR, list[i]!.radius);
  }

  applySoftSepSpatial({
    items: list,
    hash: sepHash,
    cellSize,
    getId: (e) => e.id,
    getXY: (e) => ({ x: e.x, y: e.y }),
    queryRadius: (a) => (a.radius + maxR) * sepRadiusMul,
    sepRadiusFor: (a, b) => (a.radius + b.radius) * sepRadiusMul,
    sepStrength,
    sepTopK: k,
    sepMaxImpulse: maxImpulse,
    walkEnter,
    getIntent: (e) => ({ intentVx: e.intentVx, intentVy: e.intentVy }),
    skipSubject: (a) => a.role === "weapon",
    impulseScale: (a) => {
      const prefSpd = Math.hypot(a.intentVx, a.intentVy);
      return prefSpd < walkExit ? restMul : 1;
    },
    onIntegrate: (a, sx, sy) => {
      a.vx += sx * damp;
      a.vy += sy * damp;
    },
    scratchNear: sepQueryScratch,
    scratchBuf: sepNeighborsScratch,
  });
}

function animHoldSec(): number {
  return (
    Number((C.crowd as { animHoldSec?: number }).animHoldSec) || 0.28
  );
}

/**
 * Walk/idle transitions with hold + arrived lock.
 * Does not reset animTime on locomotion flips (keeps bob phase stable).
 */
export function stepLocomotionAnim(
  e: SimEntity,
  time: number,
  prefSpd: number,
  walkEnter: number,
  walkExit: number,
  holdSec: number,
): void {
  stepLocomotionAnimEngine(e, time, prefSpd, walkEnter, walkExit, holdSec);
}

function integrateMotion(world: WorldState, dt: number): void {
  const { walkEnter, walkExit } = C.crowd;
  const hold = animHoldSec();
  for (const e of world.entities) {
    if (e.role === "weapon") {
      e.vx = 0;
      e.vy = 0;
      e.intentVx = 0;
      e.intentVy = 0;
    }
    e.x += e.vx * dt;
    e.y += e.vy * dt;
    applyFacingFromIntent(e, e.intentVx, world.time);

    const prefSpd = Math.hypot(e.intentVx, e.intentVy);
    e.animTime += dt;

    if (e.animState === "attack") {
      if (e.animTime >= swingSecFor(e.role)) {
        e.animState =
          e.arrived || prefSpd < walkExit ? "idle" : "walk";
        e.animHoldUntil = world.time + hold;
      }
      continue;
    }

    stepLocomotionAnim(e, world.time, prefSpd, walkEnter, walkExit, hold);
  }
}

/** Per-team list of units that team may attack this tick (scratch-reused). */
const scratchByTeamBuckets = new Map<number, SimEntity[]>();
const scratchEnemyByTeam = new Map<number, SimEntity[]>();
const scratchFreeLists: SimEntity[][] = [];
const scratchAliveIds = new Set<number>();
const scratchMasters = new Map<number, SimEntity>();
const scratchHeroesByTeam = new Map<number, SimEntity[]>();
const scratchById = new Map<number, SimEntity>();
const scratchSnapshot: SimEntity[] = [];

function takeScratchList(): SimEntity[] {
  const list = scratchFreeLists.pop();
  if (list) {
    list.length = 0;
    return list;
  }
  return [];
}

function recycleMapLists(map: Map<number, SimEntity[]>): void {
  for (const list of map.values()) {
    list.length = 0;
    scratchFreeLists.push(list);
  }
  map.clear();
}

function buildEnemyLists(world: WorldState): Map<number, SimEntity[]> {
  recycleMapLists(scratchByTeamBuckets);
  for (const e of world.entities) {
    let bucket = scratchByTeamBuckets.get(e.teamId);
    if (!bucket) {
      bucket = takeScratchList();
      scratchByTeamBuckets.set(e.teamId, bucket);
    }
    bucket.push(e);
  }
  recycleMapLists(scratchEnemyByTeam);
  for (const team of world.teams) {
    const list = takeScratchList();
    for (const [tid, ents] of scratchByTeamBuckets) {
      if (tid === team.id) continue;
      if (!canAttack(world, team.id, tid)) continue;
      for (let i = 0; i < ents.length; i++) list.push(ents[i]!);
    }
    scratchEnemyByTeam.set(team.id, list);
  }
  return scratchEnemyByTeam;
}

function heroAuraMul(
  e: SimEntity,
  heroesByTeam: Map<number, SimEntity[]>,
): number {
  if (e.role === "master" || e.role === "hero" || e.role === "weapon")
    return 1;
  const heroes = heroesByTeam.get(e.teamId);
  if (!heroes || heroes.length === 0) return 1;
  const auraR = C.units.hero.auraRadius ?? 120;
  for (let i = 0; i < heroes.length; i++) {
    const h = heroes[i]!;
    if (dist(h.x, h.y, e.x, e.y) <= auraR) {
      return C.units.hero.auraSpeedMul ?? 1.15;
    }
  }
  return 1;
}

function refreshAliveIds(world: WorldState): Set<number> {
  scratchAliveIds.clear();
  for (const e of world.entities) scratchAliveIds.add(e.id);
  return scratchAliveIds;
}

/** Aim weapon toward combat target only when engaged; else move intent.
 *  Distant targetId while marching caused walk weapon tremble. */
export function updateAimAngles(world: WorldState, dt: number): void {
  const eps = C.crowd.walkExit;
  const turnRate =
    Number((C.crowd as { aimTurnRate?: number }).aimTurnRate) || 7;
  const engageMul =
    Number((C.crowd as { aimEngageMul?: number }).aimEngageMul) || 1.5;
  const maxStep = Math.max(0.05, turnRate) * Math.max(0, dt);
  scratchById.clear();
  for (const e of world.entities) scratchById.set(e.id, e);

  for (const e of world.entities) {
    let desired: number | null = null;
    const tid = e.targetId;
    const t = tid != null ? scratchById.get(tid) : undefined;
    const combatAim =
      t != null &&
      shouldCombatAim(
        e.animState,
        Math.hypot(t.x - e.x, t.y - e.y),
        e.atkRange,
        engageMul,
      );
    if (combatAim && t) {
      desired = Math.atan2(t.y - e.y, t.x - e.x);
    } else {
      const spd = Math.hypot(e.intentVx, e.intentVy);
      if (spd >= eps) desired = Math.atan2(e.intentVy, e.intentVx);
    }
    if (desired == null) continue;

    let delta = desired - e.aimAngle;
    while (delta > Math.PI) delta -= Math.PI * 2;
    while (delta < -Math.PI) delta += Math.PI * 2;
    if (Math.abs(delta) <= maxStep) {
      e.aimAngle = desired;
    } else {
      e.aimAngle += Math.sign(delta) * maxStep;
    }
  }
}

/** Point weapon at foe only in attack clip or within engage band. */
export function shouldCombatAim(
  animState: string,
  distToTarget: number,
  atkRange: number,
  engageMul = 1.5,
): boolean {
  if (animState === "attack") return true;
  return distToTarget <= Math.max(0, atkRange) * Math.max(1, engageMul);
}

export function tickMovementCombat(world: WorldState, dt: number): void {
  scratchMasters.clear();
  recycleMapLists(scratchHeroesByTeam);
  for (const e of world.entities) {
    if (e.role === "master") scratchMasters.set(e.teamId, e);
    if (e.role === "hero") {
      let hs = scratchHeroesByTeam.get(e.teamId);
      if (!hs) {
        hs = takeScratchList();
        scratchHeroesByTeam.set(e.teamId, hs);
      }
      hs.push(e);
    }
  }

  const enemyByTeam = buildEnemyLists(world);
  scratchSnapshot.length = 0;
  for (let i = 0; i < world.entities.length; i++) {
    scratchSnapshot.push(world.entities[i]!);
  }
  let alive = refreshAliveIds(world);
  tickMoveOrders(world);

  for (const e of scratchSnapshot) {
    if (!alive.has(e.id)) continue;
    const team = world.teams[e.teamId];
    if (!team?.alive) continue;
    const master = scratchMasters.get(e.teamId);
    if (!master || !alive.has(master.id)) continue;

    e.atkCd = Math.max(0, e.atkCd - dt);
    const speedMul = heroAuraMul(e, scratchHeroesByTeam);
    const enemies = enemyByTeam.get(e.teamId) ?? [];
    tryInterruptMarchOnThreat(e, enemies);
    const deferCombat = shouldDeferCombat(world, team, e);

    if (e.role === "master") {
      setDesiredToward(e, team.goalX, team.goalY, speedMul, world.time);
      onUnitMaybeArrived(world, e);
      const near = pickCombatTarget(e, master, enemies);
      e.targetId =
        near && dist(e.x, e.y, near.x, near.y) < e.atkRange + 120
          ? near.id
          : null;
      if (!deferCombat && near && alive.has(near.id)) {
        if (tryFireAttack(world, e, near, enemies, speedMul)) {
          alive = refreshAliveIds(world);
        }
      }
      continue;
    }

    if (e.role === "weapon") {
      e.vx = 0;
      e.vy = 0;
      e.intentVx = 0;
      e.intentVy = 0;
      if (deferCombat) {
        e.targetId = null;
        continue;
      }
      const tgt = pickCombatTarget(e, master, enemies);
      e.targetId = tgt?.id ?? null;
      if (tgt && tryFireAttack(world, e, tgt, enemies, speedMul)) {
        applyFacingFromIntent(e, tgt.x - e.x, world.time);
        alive = refreshAliveIds(world);
        const knock = (C.units.weapon.knockback ?? 80) * 0.15;
        if (knock > 0) {
          for (const o of world.entities) {
            if (o.teamId === e.teamId) continue;
            if (!canAttack(world, e.teamId, o.teamId)) continue;
            if (dist(e.x, e.y, o.x, o.y) > e.atkRange) continue;
            const ang = Math.atan2(o.y - e.y, o.x - e.x);
            o.x += Math.cos(ang) * knock;
            o.y += Math.sin(ang) * knock;
          }
        }
      }
      continue;
    }

    if (deferCombat) {
      e.targetId = null;
      const dMaster = dist(e.x, e.y, master.x, master.y);
      const gx = team.goalX;
      const gy = team.goalY;
      let tx = gx;
      let ty = gy;
      if (dMaster > e.leash) {
        const bias = leashPullbackGoalBias();
        tx = master.x + (gx - master.x) * bias;
        ty = master.y + (gy - master.y) * bias;
      }
      const slot = troopSlotOffset(e);
      setDesiredToward(e, tx + slot.x, ty + slot.y, speedMul, world.time);
      onUnitMaybeArrived(world, e);
      continue;
    }

    const tgt = pickCombatTarget(e, master, enemies);
    e.targetId = tgt?.id ?? null;
    const profile = hitProfileFor(e.role);
    const engage =
      tgt != null &&
      alive.has(tgt.id) &&
      shouldEngage(e, master, tgt, profile);

    if (engage && tgt) {
      const { killed } = steerCombat(
        world,
        e,
        tgt,
        enemies,
        speedMul,
        profile,
      );
      if (killed) alive = refreshAliveIds(world);
    } else {
      const dMaster = dist(e.x, e.y, master.x, master.y);
      const gx = team.goalX;
      const gy = team.goalY;
      let tx = gx;
      let ty = gy;
      if (dMaster > e.leash) {
        const bias = leashPullbackGoalBias();
        tx = master.x + (gx - master.x) * bias;
        ty = master.y + (gy - master.y) * bias;
      }
      const slot = troopSlotOffset(e);
      setDesiredToward(e, tx + slot.x, ty + slot.y, speedMul, world.time);
      onUnitMaybeArrived(world, e);
    }
  }

  applySoftSeparation(world);
  integrateMotion(world, dt);
  updateAimAngles(world, dt);

  for (const e of world.entities) {
    const inset = Math.max(0, e.radius);
    const p = clampToPlay(world, e.x, e.y, inset);
    e.x = p.x;
    e.y = p.y;
  }
}
